import React from "react";
import { fetchQuery, graphql } from "react-relay";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faInfoCircle, faLocationCrosshairs } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop } from "lodash";
import { commitMutation } from "relay-runtime";

import { AppContext } from "~/common";
import UserSelector, { type SingleValueType, type UserSelectorOption } from "~/common/UserSelect/UserSelector";
import { Radio } from "~/feedback/FormInputs/RadioInput";
import { confirm } from "~/lib/prompts";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { withSuspense } from "~/lib/suspense";
import { environment } from "~/models";
import {
	type BrowseChannelType,
	type ChannelActiveSmartTrackerType,
	ChannelIcon,
	type ChannelMember,
	leaveChannelMutation,
	leaveConfirmation,
} from "~/recordings/BrowseChannelsModal/BrowseChannelsModal";
import { eventEmitter as recordingsEventEmitter } from "~/recordings/events";
import { useTrackersFeatureFlagOn } from "~/trackers/featureFlag";
import { Avatar, Button, LinkButton } from "~/ui";
import { SimpleInput } from "~/ui/Input";
import { ComplexTabs, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import toast from "~/ui/toast";

import type { BrowseChannelsModalLeaveChannelMutation } from "~/__generated__/BrowseChannelsModalLeaveChannelMutation.graphql";
import type { CreateEditChannelModalCreateMutation } from "~/__generated__/CreateEditChannelModalCreateMutation.graphql";
import type { CreateEditChannelModalDeleteMutation } from "~/__generated__/CreateEditChannelModalDeleteMutation.graphql";
import type { CreateEditChannelModalEditMutation } from "~/__generated__/CreateEditChannelModalEditMutation.graphql";
import type { CreateEditChannelModalIdQuery } from "~/__generated__/CreateEditChannelModalIdQuery.graphql";
import CreateEditChannelModalInitializationQueryGraphql, {
	type CreateEditChannelModalInitializationQuery,
} from "~/__generated__/CreateEditChannelModalInitializationQuery.graphql";

import trackerStyles from "~/trackers/TrackerItem.module.css";

import styles from "./CreateEditChannelModal.module.css";

type NewChannelProps = {
	name?: string;
};

export type UpdatedChannel = { id: string; name: string; isPrivate: boolean };

type CreateChannelModalProps = {
	readOnly?: boolean;
	onClose: (result?: UpdatedChannel) => void;
	refetchChannels: () => void;
	newChannel?: NewChannelProps;
	editedChannel?: BrowseChannelType;
	// NOTE: autoPublishRecordingSettings is optional and can be passed in to avoid fetching it again. By default, the
	//       component will fetch it if the property is UNDEFINED. Please note that NULL is a valid value for this prop.
	autoPublishSettings?: CreateEditChannelModalIdQuery["response"]["viewer"]["autoPublishRecordingSettings"];
	startingTab?: "details" | "members" | "trackers";
};

type Visibility = "public" | "private";

const createChannelMutation = graphql`
	mutation CreateEditChannelModalCreateMutation(
		$name: String!
		$isPrivate: Boolean!
		$description: String
		$members: [ID!]
	) {
		createChannel(name: $name, isPrivate: $isPrivate, description: $description, members: $members) {
			channel {
				...BrowseChannelsModalChannel @relay(mask: false)
			}
		}
	}
`;

const editChannelMutation = graphql`
	mutation CreateEditChannelModalEditMutation(
		$channelId: ID!
		$name: String!
		$isPrivate: Boolean!
		$description: String
		$members: [ID!]
	) {
		editChannel(
			channelId: $channelId
			name: $name
			isPrivate: $isPrivate
			description: $description
			members: $members
		) {
			channel {
				...BrowseChannelsModalChannel @relay(mask: false)
			}
		}
	}
`;

const deleteChannelMutation = graphql`
	mutation CreateEditChannelModalDeleteMutation($channelId: ID!) {
		deleteChannel(channelId: $channelId) {
			ok
		}
	}
`;

type ChannelDetailsTabProps = {
	readOnly?: boolean;
	channelName: string;
	description: string;
	visibility: Visibility;

	setChannelName: (channelName: string) => void;
	setDescription: (description: string) => void;
	setVisibility: (visibility: Visibility) => void;
};

function ChannelDetailsTab(props: ChannelDetailsTabProps) {
	const { channelName, description, visibility, setChannelName, setDescription, setVisibility, readOnly } = props;

	const setPublic = React.useCallback(() => {
		setVisibility("public");
	}, [setVisibility]);

	const setPrivate = React.useCallback(() => {
		setVisibility("private");
	}, [setVisibility]);

	return (
		<div>
			<div className={styles.inputGroup}>
				<label htmlFor="channel-name" className={styles.label}>
					Channel name
				</label>

				<SimpleInput
					id="channel-name"
					value={channelName}
					onValueChange={setChannelName}
					placeholder="e.g. Customer Feedback"
					disabled={readOnly}
					name="channel_name_edit"
				/>

				{!readOnly && <div className={styles.subLabel}>Use a name that is easy to find and understand.</div>}
			</div>

			<div className={styles.inputGroup}>
				<label htmlFor="channel-description" className={styles.label}>
					Description (optional)
				</label>

				<SimpleInput
					id="channel-description"
					value={description}
					onValueChange={setDescription}
					placeholder={readOnly ? "No description" : "Add a short description"}
					disabled={readOnly}
					name="channel_description_edit"
				/>

				{!readOnly && <div className={styles.subLabel}>Describe the main objective or context of the channel.</div>}
			</div>

			<div className={styles.inputGroup}>
				<label className={styles.label} style={{ marginBottom: "8px" }}>
					Visibility
				</label>

				<div className={styles.radioGroup}>
					<label className={styles.radioRow}>
						<Radio
							name="visibility"
							value="public"
							className={styles.radioInput}
							checked={visibility === "public"}
							onChange={setPublic}
							disabled={readOnly}
						/>

						<ChannelIcon isPrivate={false} readOnly={readOnly} />

						<div>
							<div className={styles.radioLabel}>Public</div>
							<div className={styles.radioSubText}>Anyone in your workspace can access</div>
						</div>
					</label>
				</div>

				<div className={styles.radioGroup}>
					<label className={styles.radioRow}>
						<Radio
							name="visibility"
							value="private"
							className={styles.radioInput}
							checked={visibility === "private"}
							onChange={setPrivate}
							disabled={readOnly}
						/>

						<ChannelIcon isPrivate={true} readOnly={readOnly} />

						<div>
							<div className={styles.radioLabel}>Private</div>
							<div className={styles.radioSubText}>Can only be viewed or modified by invitation</div>
						</div>
					</label>
				</div>
			</div>
		</div>
	);
}

function MembersList({
	isCreate,
	members,
	removeMember,
	readOnly,
}: {
	isCreate: boolean;
	members: ChannelMember[];
	removeMember: (member: ChannelMember) => void;
	readOnly?: boolean;
}) {
	const onRemove = React.useCallback(
		(member: ChannelMember) => () => {
			removeMember(member);
		},
		[removeMember],
	);
	const { user } = React.useContext(AppContext);

	if (members.length === 0 && !isCreate) {
		return null;
	}

	return (
		<div className={styles.membersList}>
			<div className={styles.membersHeader}>Channel members</div>

			<div className={styles.membersWrapper}>
				{isCreate && user && (
					<div key={user.id} className={styles.memberRow}>
						<div className={styles.memberInfo}>
							<div className={styles.memberAvatar}>
								<Avatar user={user} size="medium" />
							</div>

							<div>
								<div className={styles.memberName}>{user.fullName} (you)</div>
								<div className={styles.memberEmail}>{user.email}</div>
							</div>
						</div>
					</div>
				)}

				{members.map(member => (
					<div key={member.id} className={styles.memberRow}>
						<div className={styles.memberInfo}>
							<div className={styles.memberAvatar}>
								<Avatar user={member} size="medium" />
							</div>

							<div>
								<div className={styles.memberName}>{member.fullName}</div>
								<div className={styles.memberEmail}>{member.email}</div>
							</div>
						</div>

						{!readOnly && (
							<Button
								disabled={readOnly}
								variant="tertiary"
								size="slim"
								className={styles.memberRemoveButton}
								onClick={onRemove(member)}
							>
								Remove
								<FontAwesomeIcon icon={faTimes} />
							</Button>
						)}
					</div>
				))}
			</div>
		</div>
	);
}

type ChannelMembersTabProps = {
	isCreate: boolean;
	selectedMembers: ChannelMember[];
	removeMember: (member: ChannelMember) => void;
	addMember: (member: ChannelMember) => void;
	readOnly?: boolean;
};

function ChannelMembersTab({ isCreate, selectedMembers, removeMember, addMember, readOnly }: ChannelMembersTabProps) {
	const onChange = React.useCallback(
		(newUser: SingleValueType<UserSelectorOption>) => {
			if (!newUser || !newUser.avatar) {
				return;
			}

			addMember({ ...newUser.avatar, email: newUser.avatar.email || "" });
		},
		[addMember],
	);

	return (
		<div className={styles.membersTab}>
			{!readOnly && (
				<div className={styles.inputGroup}>
					<label className={styles.label}>Add members</label>

					<UserSelector
						value={null}
						selectedUser={null}
						key={selectedMembers.length}
						excludeUserIDs={selectedMembers.map(member => member.id)}
						styles={{
							valueContainer: prev => ({
								...prev,
								padding: "7px 8px 7px 12px",
							}),
							container: prev => ({
								...prev,
								marginBottom: "4px",
							}),
						}}
						onChange={onChange}
					/>

					<div className={styles.subLabel}>All channel members can see and contribute to channel</div>
				</div>
			)}

			<MembersList isCreate={isCreate} members={selectedMembers} removeMember={removeMember} readOnly={readOnly} />
		</div>
	);
}

graphql`
	query CreateEditChannelModalInitializationQuery {
		viewer {
			autoPublishRecordingSettings {
				id
				enabled
				externalRecordingsChannel {
					id
				}
				internalRecordingsChannel {
					id
				}
			}
		}
	}
`;

type ChannelTrackersTabProps = {
	activeSmartTrackers: readonly ChannelActiveSmartTrackerType[];
};

function ChannelTrackersTab({ activeSmartTrackers }: ChannelTrackersTabProps) {
	return (
		<div>
			{activeSmartTrackers.length === 0 ? (
				<div className={styles.noTrackersText}>
					<FontAwesomeIcon icon={faLocationCrosshairs} className={styles.infoTextIcon} />
					No active trackers on this channel
				</div>
			) : (
				<div>
					<label className={styles.label}>Active trackers on channel</label>
					<div className={styles.infoText}>External meeting recaps added to this channel are analyzed for:</div>

					<div className={styles.trackersList}>
						{activeSmartTrackers.map(tracker => (
							<div key={tracker.name} className={cx(trackerStyles.trackerCard, trackerStyles.compact)}>
								<FontAwesomeIcon icon={faLocationCrosshairs} className={trackerStyles.trackerIcon} />

								<div className={trackerStyles.trackerName}>{tracker.name}</div>
								<div className={trackerStyles.trackerValues}>{tracker.triggerPhrasesOrWords.join(", ")}</div>
							</div>
						))}
					</div>
					<div className={styles.infoText}>
						<FontAwesomeIcon icon={faInfoCircle} className={styles.infoTextIcon} />
						Meetings shared in multiple channels may include trackers not listed here
					</div>
				</div>
			)}
			<div className={styles.manageTrackersButton}>
				<LinkButton to="/trackers" variant="secondary">
					Manage all trackers
				</LinkButton>
			</div>
		</div>
	);
}

export default function CreateEditChannelModal({
	readOnly,
	onClose,
	refetchChannels,
	newChannel,
	editedChannel,
	autoPublishSettings,
	startingTab,
}: CreateChannelModalProps) {
	const isTrackersFeatureFlagOn = useTrackersFeatureFlagOn();

	const createTabs = [
		{
			label: "Details",
			key: "details" as const,
		},
		{
			label: "Members",
			key: "members" as const,
		},
	];
	const trackersTab = [
		{
			label: "Trackers",
			key: "trackers" as const,
		},
	];
	const editTabs = isTrackersFeatureFlagOn ? [...createTabs, ...trackersTab] : createTabs;

	const [selectedTab, _setSelectedTab] = React.useState<"details" | "members" | "trackers">(startingTab ?? "details");

	const [channelName, setChannelName] = React.useState(newChannel?.name || editedChannel?.name || "");
	const [description, setDescription] = React.useState(editedChannel?.description || "");
	const [visibility, setVisibility] = React.useState<Visibility>(editedChannel?.isPrivate ? "private" : "public");

	const [members, setMembers] = React.useState<ChannelMember[]>((editedChannel?.members as ChannelMember[]) || []);

	const [shouldShowNextButton, setShouldShowNextButton] = React.useState(!editedChannel);
	const [fieldsAreNotFilled, setFieldsAreNotFilled] = React.useState(!editedChannel);
	const [isLoading, setIsLoading] = React.useState(false);
	const [autoPublishSettingsState, setAutoPublishSettingsState] = React.useState(autoPublishSettings);
	const buttonDisabled = fieldsAreNotFilled || isLoading;

	const onNextPressed = React.useCallback(() => {
		_setSelectedTab("members");
		setShouldShowNextButton(false);
	}, []);

	const setSelectedTab = React.useCallback((value: "details" | "members" | "trackers") => {
		_setSelectedTab(value);
		setShouldShowNextButton(false);
	}, []);

	React.useEffect(() => {
		setFieldsAreNotFilled(!channelName);
	}, [channelName]);

	const addMember = React.useCallback((member: ChannelMember) => {
		setMembers(prevMembers => [...prevMembers, member]);
	}, []);

	const removeMember = React.useCallback((member: ChannelMember) => {
		setMembers(prevMembers => prevMembers.filter(m => m.id !== member.id));
	}, []);

	React.useEffect(() => {
		// NOTE: autoPublishSettings can be NULL. We only trigger the fetch if it's undefined.
		if (autoPublishSettingsState === undefined) {
			fetchQuery<CreateEditChannelModalInitializationQuery>(
				environment,
				CreateEditChannelModalInitializationQueryGraphql,
				{},
			)
				.toPromise()
				.then(result => {
					if (result === undefined) {
						throw new Error("Unexpected failure from the initialization query");
					}
					setAutoPublishSettingsState(result.viewer.autoPublishRecordingSettings);
				});
		}
	}, [autoPublishSettingsState]);

	const onSave = React.useCallback(() => {
		if (buttonDisabled) {
			return;
		}

		setIsLoading(true);

		if (editedChannel) {
			commitMutation<CreateEditChannelModalEditMutation>(environment, {
				mutation: editChannelMutation,
				variables: {
					channelId: editedChannel.id,
					name: channelName,
					description,
					isPrivate: visibility === "private",
					members: members.map(member => member.id),
				},
				onCompleted: (_response, errors) => {
					setIsLoading(false);

					if (errors) {
						toast.error(errors[0].message);
						return;
					}

					refetchChannels();
					onClose();
					dispatchAnalyticsTrack("Edit channel", { localtion: "CreateEditChannelModal" });
					toast.success("Edited the channel!");
				},
			});
		} else {
			commitMutation<CreateEditChannelModalCreateMutation>(environment, {
				mutation: createChannelMutation,
				variables: {
					name: channelName,
					description,
					isPrivate: visibility === "private",
					members: members.map(member => member.id),
				},
				onCompleted: (_response, errors) => {
					setIsLoading(false);

					if (errors) {
						toast.error(errors[0].message);
						return;
					}

					if (!_response.createChannel) {
						toast.error("Failed to create the channel");
						return;
					}

					const { id, name, isPrivate } = _response.createChannel.channel;

					refetchChannels();
					onClose({ id, name, isPrivate });
					dispatchAnalyticsTrack("Create channel", { localtion: "CreateEditChannelModal" });
					toast.success("Created the channel!");
				},
			});
		}
	}, [buttonDisabled, editedChannel, channelName, description, visibility, members, refetchChannels, onClose]);

	const onDelete = React.useCallback(async () => {
		if (!editedChannel) {
			return;
		}

		const wannaDelete = await confirm(`Are you sure you want to delete the channel "${editedChannel.name}"`, {
			yesText: "Yes, delete",
			title: "Delete channel?",
			yesProps: { variant: "destructive" },
		});

		if (!wannaDelete) {
			return;
		}

		commitMutation<CreateEditChannelModalDeleteMutation>(environment, {
			mutation: deleteChannelMutation,
			variables: {
				channelId: editedChannel.id,
			},
			onCompleted: () => {
				refetchChannels();
				onClose();
				toast.success("Deleted the channel!");
				dispatchAnalyticsTrack("Delete channel", { localtion: "CreateEditChannelModal" });
				recordingsEventEmitter.emit("Channels.deleted", editedChannel.id);
			},
		});
	}, [editedChannel, onClose, refetchChannels]);

	const onLeave = React.useCallback(async () => {
		if (!editedChannel) return;

		const isUsedInAutoPublish = Boolean(
			autoPublishSettingsState?.enabled &&
				(autoPublishSettingsState.externalRecordingsChannel?.id === editedChannel.id ||
					autoPublishSettingsState.internalRecordingsChannel?.id === editedChannel.id),
		);
		if (!(await leaveConfirmation(editedChannel, isUsedInAutoPublish))) return;

		commitMutation<BrowseChannelsModalLeaveChannelMutation>(environment, {
			mutation: leaveChannelMutation,
			variables: { channelId: editedChannel.id },
			onCompleted: () => {
				toast.success("Left the channel!");
				refetchChannels();
				onClose();
				dispatchAnalyticsTrack("Leave channel", { location: "CreateEditChannelModal" });
				recordingsEventEmitter.emit("Channels.left", editedChannel.id);
			},
		});
	}, [editedChannel, onClose, refetchChannels, autoPublishSettingsState]);

	const dismissModal = React.useCallback(() => onClose(), [onClose]);

	return (
		<Modal onClose={dismissModal} width="500px" height={editedChannel ? "480px" : "460px"}>
			<Modal.Header noBottomBorder onClose={dismissModal}>
				<Modal.Title>
					{readOnly ? "Channel details" : editedChannel ? editedChannel.name : "Create a channel"}
				</Modal.Title>
			</Modal.Header>

			<Modal.Body noPadding className={styles.modalBody}>
				<div className={styles.tabsWrapper}>
					<div className={styles.tabsBorder} />
					<ComplexTabs small tabs={editedChannel ? editTabs : createTabs} tab={selectedTab} setTab={setSelectedTab} />
				</div>

				<div className={cx(styles.realModalBody, { [styles.darkBody]: selectedTab === "trackers" })}>
					{selectedTab === "details" ? (
						<ChannelDetailsTab
							channelName={channelName}
							description={description}
							visibility={visibility}
							setChannelName={setChannelName}
							setDescription={setDescription}
							setVisibility={setVisibility}
							readOnly={readOnly}
						/>
					) : selectedTab === "members" ? (
						<ChannelMembersTab
							isCreate={!editedChannel}
							selectedMembers={members}
							addMember={addMember}
							removeMember={removeMember}
							readOnly={readOnly}
						/>
					) : (
						<ChannelTrackersTab activeSmartTrackers={editedChannel?.activeSmartTrackers ?? []} />
					)}
				</div>
			</Modal.Body>

			{selectedTab !== "trackers" && (
				<Modal.Footer noTopBorder={!editedChannel}>
					{readOnly ? (
						<Button variant="primary" onClick={dismissModal}>
							Close
						</Button>
					) : (
						<>
							{editedChannel && (
								<Modal.FooterSecondaryActions>
									<Button variant="destructive" onClick={onLeave}>
										Leave
									</Button>

									<Button variant="tertiary" onClick={onDelete} className={styles.deleteButton}>
										Delete channel
									</Button>
								</Modal.FooterSecondaryActions>
							)}

							{!editedChannel && (
								<Button variant="secondary" onClick={dismissModal}>
									Cancel
								</Button>
							)}

							{shouldShowNextButton ? (
								<Button variant="primary" onClick={onNextPressed} disabled={buttonDisabled}>
									Next
								</Button>
							) : (
								<Button variant="primary" onClick={onSave} disabled={buttonDisabled} className={styles.saveButton}>
									{isLoading && <LoadingSpinner inline size="2xs" dimmer={false} />}{" "}
									{editedChannel ? "Save" : "Create channel"}
								</Button>
							)}
						</>
					)}
				</Modal.Footer>
			)}
		</Modal>
	);
}

export const CreateChannelModal = withSuspense(
	<Modal onClose={noop}>
		<LoadingSpinner />
	</Modal>,
	CreateEditChannelModal,
);

function EditChannelByIdModalInternal({
	channelId,
	onClose,
	refetchChannels,
	startingTab,
	readOnly,
}: {
	channelId: string;
	onClose: () => void;
	refetchChannels: () => void;
	startingTab?: "details" | "members" | "trackers";
	readOnly?: boolean;
}) {
	const response = useLazyLoadQuery<CreateEditChannelModalIdQuery>(
		graphql`
			query CreateEditChannelModalIdQuery($channelId: ID!) {
				node(id: $channelId) {
					... on Channel {
						...BrowseChannelsModalChannel @relay(mask: false)
					}
				}
				viewer {
					autoPublishRecordingSettings {
						id
						enabled
						externalRecordingsChannel {
							id
						}
						internalRecordingsChannel {
							id
						}
					}
				}
			}
		`,
		{ channelId },
		{ fetchPolicy: "store-and-network" },
	);

	if (!response.node) {
		return null;
	}

	return (
		<CreateEditChannelModal
			readOnly={readOnly}
			editedChannel={response.node as BrowseChannelType}
			autoPublishSettings={response.viewer.autoPublishRecordingSettings}
			onClose={onClose}
			startingTab={startingTab}
			refetchChannels={refetchChannels}
		/>
	);
}

export const EditChannelByIdModal = withSuspense(
	<Modal onClose={noop}>
		<LoadingSpinner />
	</Modal>,
	EditChannelByIdModalInternal,
);
